// Anket Yönetimi sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'surveys.list';

export const surveysListGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'Anket sistemi nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Müşterilerinize QR kod ile anket sorarsınız: soruyu oluşturur, tekrarlı bir göreve bağlar, QR kodu yazdırır ve sonuçları izlersiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  navItems: [
    t(`${G}.nav.charts`, 'Anket Grafikleri'),
    t(`${G}.nav.surveys`, 'Anketler'),
    t(`${G}.nav.reports`, 'Anket Raporları'),
    t(`${G}.nav.qrFeedback`, 'QR Menü Geri Bildirimleri'),
  ],
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık ve ekleme düğmeleri'),
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Soru listesi'),
    footer: t(`${G}.regions.footer`, 'Görev listesi (sağda / telefonda altta)'),
  },
  steps: [
    {
      region: 'tabs',
      title: t(`${G}.s1.title`, '1. Sekmeleri tanıyın'),
      text: t(`${G}.s1.text`, 'Anket Grafikleri genel istatistikleri ve grafikleri gösterir. Soru oluşturma ve görev bağlama işleri Anketler sekmesinde yapılır. Anket Raporları tüm yanıtları listeler; QR Menü Geri Bildirimleri QR menüden gelen yorumları toplar.'),
      tips: [
        t(`${G}.s1.tip1`, 'Şube bazlı: şube yöneticileri yalnız kendi şubelerine ait anket ve görevleri görür.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s2.title`, '2. Anket sorusu oluşturun'),
      text: t(`${G}.s2.text`, 'Anketler sekmesinde "Anket Sorusu Ekle" düğmesine tıklayın. Soru Metni alanına müşteriye soracağınız soruyu yazın (ör. "Hizmetimizden memnun kaldınız mı?"). Ardından cevapları ekleyin ve "Kaydet" ile soruyu oluşturun.'),
      tips: [
        t(`${G}.s2.tip1`, 'Her cevap için metni yazın (ör. "Çok Memnunum", "Memnunum", "Kararsızım", "Memnun Değilim") ve tipini seçin.'),
        t(`${G}.s2.tip2`, 'Cevap tipleri: Olumlu – iyi geri dönüşler, Olumsuz – kötü geri dönüşler, Nötr – tarafsız cevaplar için.'),
        t(`${G}.s2.tip3`, 'Bir soruya en az 2 cevap eklemelisiniz; üst sınır yoktur.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s3.title`, '3. Tekrarlı anket görevi ekleyin'),
      text: t(`${G}.s3.text`, '"Yeni Anket Ekle" düğmesi görev ekleme penceresini açar. Görev Adı (ör. "Günlük Müşteri Memnuniyet Anketi"), isteğe bağlı Açıklama, anketi yapacak personel ve varsa şubeyi girin. "Tekrarlı Görev"i işaretleyip "Kaydet" ile görevi oluşturun.'),
      tips: [
        t(`${G}.s3.tip1`, 'Tekrar Tipi: Günlük, Haftalık veya Aylık. Tekrar Saatleri: anketin yapılacağı saatler (ör. 10:00, 14:00, 18:00).'),
        t(`${G}.s3.tip2`, 'Tolerans Süresi: görevin kaç dakika önceden başlatılabileceği (varsayılan 15 dk).'),
        t(`${G}.s3.tip3`, 'Önemli: anket görevleri mutlaka tekrarlı (günlük / haftalık / aylık) olmalıdır. Tek seferlik görevler anket sisteminde çalışmaz.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Soruya görev bağlayın, soruyu yönetin'),
      text: t(`${G}.s4.text`, 'Soldaki soru listesinden bir soruya tıklayın; Anket Detayları penceresi açılır. Sağ üstteki "Görev Ekle" simgesiyle görev seçim penceresini açın, bir veya birden çok tekrarlı görevi işaretleyip "Kaydet"e basın. Seçtiğiniz görevler artık bu soruya bağlıdır.'),
      tips: [
        t(`${G}.s4.tip1`, 'Listede yalnız günlük, haftalık ve aylık tekrarlı görevler görünür.'),
        t(`${G}.s4.tip2`, 'Bir soruya birden fazla görev bağlayabilirsiniz; böylece aynı anketi farklı lokasyonlar, personeller veya şubeler için kullanırsınız.'),
        t(`${G}.s4.tip3`, 'Düzenle simgesi: soru metnini değiştirin, cevap ekleyin / çıkarın / düzenleyin, cevap tiplerini değiştirin.'),
        t(`${G}.s4.tip4`, 'Sil simgesi: anket sorusu kalıcı olarak silinir.'),
        t(`${G}.s4.tip5`, 'Görev listesindeki sil simgesi görevi anketten çıkarır; görev silinmez, yalnız bağlantı kopar.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s5.title`, '5. QR kodu oluşturun ve yazdırın'),
      text: t(`${G}.s5.text`, 'QR kodu iki yerden alırsınız: Anket Detayları penceresinde her görevin yanındaki QR simgesinden ya da Anketler sekmesindeki görev listesinin "İşlemler" sütunundaki QR simgesinden. Açılan pencerede QR kodu görür, metin veya logo ekleyerek özelleştirir, "Yazdır" ile yazdırırsınız.'),
      tips: [
        t(`${G}.s5.tip1`, 'QR kodu PNG, PDF, SVG, EPS, DXF ve TIFF biçimlerinde de indirebilirsiniz.'),
        t(`${G}.s5.tip2`, 'QR kodu masaya, kapıya veya tezgaha yapıştırın; müşteriler telefon kamerasıyla okutarak ankete ulaşır.'),
        t(`${G}.s5.tip3`, 'Aynı QR kodu birden fazla yere basabilirsiniz; her okutma ayrı bir yanıt olarak kaydedilir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. Müşteri anketi nasıl yanıtlar?'),
      text: t(`${G}.s6.text`, 'Müşteri QR kodu telefon kamerasıyla okutur ve tarayıcıda anket sayfası açılır. Soruları görüp cevaplarını seçer, isterse geri bildirim (iletişim bilgisi, yorum vb.) yazar ve "Anketi Gönder"e basar; ekranda başarı mesajı görünür.'),
      tips: [
        t(`${G}.s6.tip1`, 'Yanıtlar otomatik olarak sisteme kaydedilir ve anında raporlara yansır; sayfayı yenilemenize gerek yoktur.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s7.title`, '7. Sonuçları görüntüleyin'),
      text: t(`${G}.s7.text`, 'Sonuçları üç yerden incelersiniz: Anket Grafikleri sekmesi, Anket Raporları sekmesi ve Anketler sekmesindeki "Excel İndir" düğmesi.'),
      tips: [
        t(`${G}.s7.tip1`, 'Anket Grafikleri: genel memnuniyet oranı, zamana göre anket trendleri ve detaylı istatistikler; şubeye göre filtreleyebilirsiniz.'),
        t(`${G}.s7.tip2`, 'Anket Raporları: tüm yanıtları ayrıntılı listede görün, tarihe / personele / ankete göre filtreleyin, müşteri geri bildirimlerini okuyun ve Excel\'e aktarın.'),
        t(`${G}.s7.tip3`, 'Excel İndir: tüm anket soruları ve cevapları bir Excel dosyasına aktarılır.'),
        t(`${G}.s7.tip4`, 'Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
      ],
    },
  ],
});
